/**
 * Coupon (promo code) — a shareable marketing code that customers enter at
 * checkout to receive a discount. Unlike vouchers (pre-paid gift cards sold to a
 * specific recipient), a coupon is a reusable code created by the business owner
 * for promotions and is subject to usage/eligibility constraints.
 */

export enum CouponDiscountTypeEnum {
  /** Percentage off the eligible services subtotal (discountValue = 0-100). */
  Percentage = 'percentage',
  /** Fixed amount (₪) off the eligible services subtotal. */
  Amount = 'amount'
}

/**
 * What a coupon may be spent on. A coupon with no types listed applies to everything,
 * which is what every coupon created before this existed does.
 */
export enum CouponEntityTypeEnum {
  Service = 'service',
  Product = 'product',
  Voucher = 'voucher',
  Bundle = 'bundle'
}

/** Hebrew labels for the entity types, for the admin selector and any summary line. */
export const COUPON_ENTITY_TYPE_LABELS: Record<CouponEntityTypeEnum, string> = {
  [CouponEntityTypeEnum.Service]: 'שירותים',
  [CouponEntityTypeEnum.Product]: 'מוצרים',
  [CouponEntityTypeEnum.Voucher]: 'שוברים',
  [CouponEntityTypeEnum.Bundle]: 'כרטיסיות'
};

export enum CouponInvalidReasonEnum {
  NotFound = 'not_found',
  Inactive = 'inactive',
  NotStarted = 'not_started',
  Expired = 'expired',
  UsageLimitReached = 'usage_limit_reached',
  PerCustomerLimitReached = 'per_customer_limit_reached',
  MinPurchaseNotMet = 'min_purchase_not_met',
  NotApplicableToServices = 'not_applicable_services',
  /** Nothing in the cart is of a type this coupon may be spent on. */
  NotApplicableToEntityTypes = 'not_applicable_entity_types',
  FirstTimeCustomersOnly = 'first_time_customers_only'
}

export interface ICouponRedemption {
  customerId?: string;
  customerPhone?: string;
  /** Set when the coupon was spent on a booking. */
  appointmentId?: string;
  /** Set when the coupon was spent buying a gift voucher. */
  voucherId?: string;
  /** Set when the coupon was spent buying a bundle pack. */
  bundlePackId?: string;
  /** Set when the coupon was spent on a direct (products-only) order. */
  productOrderId?: string;
  /** Discount amount (₪) granted by this redemption. */
  amount: number;
  redeemedAt: Date | string;
}

export interface ICoupon {
  _id?: string;
  bid: string;
  /** Customer-facing code, normalized to uppercase. Unique per business. */
  code: string;
  /** Internal description / marketing note (optional). */
  description?: string;

  discountType: CouponDiscountTypeEnum;
  /** Percentage (0-100) when type is Percentage, fixed ₪ amount when type is Amount. */
  discountValue: number;
  /** Optional cap (₪) for percentage discounts. */
  maxDiscountAmount?: number;
  /** Minimum services subtotal (₪) required for the coupon to apply. */
  minPurchaseAmount?: number;

  /**
   * Limit the coupon to certain kinds of purchase. Empty / undefined = anything.
   * Narrowing this to exclude services makes {@link applicableServiceIds} meaningless,
   * so the two are validated together.
   */
  applicableEntityTypes?: CouponEntityTypeEnum[];

  /**
   * Limit the coupon to specific services. Empty / undefined = all services.
   * Only meaningful while services are in scope — see {@link applicableEntityTypes}.
   */
  applicableServiceIds?: string[];

  validFrom?: Date | string;
  validUntil?: Date | string;

  /** Total redemptions allowed across all customers. Undefined = unlimited. */
  usageLimit?: number;
  /** Max redemptions for a single customer. Undefined = unlimited. */
  perCustomerLimit?: number;
  /** Restrict to customers who never had a completed appointment at this business. */
  firstTimeCustomersOnly?: boolean;

  active: boolean;

  /** Running total of redemptions (maintained by the backend). */
  usageCount?: number;
  redemptions?: ICouponRedemption[];

  createdAt?: string;
  updatedAt?: string;
}

export interface ICouponValidationResult {
  valid: boolean;
  reason?: CouponInvalidReasonEnum;
  couponId?: string;
  code?: string;
  /** Discount amount (₪) the coupon would apply, when valid. */
  discountAmount?: number;
  /**
   * The part of {@link discountAmount} that falls on products rather than services.
   * Services and products are charged differently (an advance percentage applies only to
   * services), so the caller has to know which side of the bill the discount came off.
   */
  productsDiscountAmount?: number;
}

/**
 * Whether the coupon may be spent on a given kind of purchase.
 * No types listed means no restriction — the default, and what every pre-existing
 * coupon does.
 */
export function couponAppliesToEntityType(
  coupon: Pick<ICoupon, 'applicableEntityTypes'>,
  type: CouponEntityTypeEnum
): boolean {
  const types = coupon.applicableEntityTypes ?? [];
  return types.length === 0 || types.includes(type);
}

/**
 * Whether a per-service restriction is meaningful for this coupon.
 *
 * Only when services are something the coupon can be spent on: narrowing a
 * products-only coupon to "haircut" describes a combination that can never occur, so the
 * admin form hides the selector and the backend rejects the pairing.
 */
export function couponSupportsServiceScope(
  coupon: Pick<ICoupon, 'applicableEntityTypes'>
): boolean {
  return couponAppliesToEntityType(coupon, CouponEntityTypeEnum.Service);
}
